import { UIElement } from '../../../shared-components/components/ui-element';
import { Component } from '../../../shared-components/decorators/component.decorator';
import { Attribute } from '../../../shared-components/decorators/attribute.decorator';
import { UIPoint } from '../point/ui-point';
import type { UIChartSeriesData } from '../charts.types';
import styles from './ui-series.css';

/**
 * @class
 * @memberof ChartsComponents
 * @element ui-series
 * @augments {UIElement}
 * @alias UISeries
 * @classdesc Represents a class for <code>ui-series</code> element.
 * This is tag abstraction for Highchart series.
 * For more details visit: https://api.highcharts.com/class-reference/Highcharts.Series
 * @property {string} icon {@attr icon} Icon for series, same as glyphs is icon.
 * @property {string} data {@attr data} Semicolor-separated (;) data for series
 * @property {string} name {@attr name} Name (label) of the series.
 * @property {boolean} hidden {@attr hidden} Sets series hidden (inactive on chart),
 * but legend is still visible.
 * @example
 * <ui-series></ui-series>
 */
@Component({ tag: 'ui-series', styles })
export class UISeries extends UIElement {
    @Attribute(String) name?: string;
    @Attribute(String) icon?: string;
    @Attribute(String) data?: string;

    /**
     * Parses the data attribute.
     * @returns {Array<Record<string, unknown>>}
     * @private
     */
    private parseDataAttribute(): Array<Record<string, unknown>> {
        const retval: Array<Record<string, unknown>> = [];
        const tmp = JSON.parse(this.data ?? '[]') as Array<
            UIChartSeriesData | number
        >;
        tmp.forEach((y) => {
            let dataObj: UIChartSeriesData | Array<number>;
            if (Array.isArray(y)) {
                dataObj = y;
            } else if (typeof y !== 'object') {
                dataObj = { y };
            } else {
                dataObj = y;
            }
            if (this.className && dataObj !== null && !Array.isArray(dataObj)) {
                dataObj.className = this.className;
            }
            retval.push(dataObj as Record<string, unknown>);
        });
        return retval;
    }

    /**
     * Converts points data to object.
     * @returns {Array<Record<string, unknown>>}
     * @private
     */
    private getDataFromPoints(): Array<Record<string, unknown>> {
        const points = this.querySelectorAll<UIPoint>('ui-point');
        return Array.from(points).map((point) => {
            return point.getAttrs();
        });
    }

    /**
     * Checks that series has ui-point children.
     * @returns {boolean}
     * @private
     */
    private hasPoints(): boolean {
        return this.querySelectorAll('ui-point').length > 0;
    }

    /**
     * Converts component's attributes to JSON object.
     * @returns {Record<string, unknown>}
     */
    toJSON(): Record<string, unknown> {
        const retval: Record<string, unknown> = {};
        for (const attr of this.attributes) {
            const name = attr.name;
            retval[name] =
                name === 'data' ? this.parseDataAttribute() : attr.nodeValue;
        }
        if (this.classList.length > 0) {
            retval.className = this.className;
            const cidx = this.getColorIndexFromClassName();
            if (cidx !== -1) {
                retval.colorIndex = cidx;
            }
        }
        if (this.hidden) {
            retval.visible = !this.hidden;
        }
        if (this.hasPoints()) {
            retval.data = this.getDataFromPoints();
        }
        return retval;
    }

    /**
     * Gets the last color index from highcharts-color-{n} css classname.
     * @returns {number}
     * @private
     */
    private getColorIndexFromClassName(): number {
        let index = -1;
        this.classList.forEach((klass) => {
            const c = parseInt(klass.replace(/highcharts-color-/g, ''));
            if (!isNaN(c)) {
                index = c;
            }
        });
        return index;
    }
}
